import React from 'react';
import { Box, Button, Grid, IconButton, Dialog } from '@material-ui/core';
import { PiVideoCamera, PiXCircle } from 'react-icons/pi';
import { useExperience } from '../contexts/experience';
import { useTheme } from '../contexts/theme';
import SingleVideoPlayer from './SingleVideoPlayer';
import { useLanguage } from '../contexts/language';

const VideoPlayerOnMap = () => {
  const { isVideoActive, videoActive, videoToBeActivated } = useExperience();
  const [openSheet, setOpenSheet] = React.useState(false);
  const { content } = useLanguage();

  const videoSeeksMapRef = React.useRef(new Map());

  React.useEffect(
    () => videoActive.forEach((video) => videoSeeksMapRef?.current?.set(video.id, 0)),
    [videoActive]
  );

  const hasVideoToBeActivated = React.useMemo(
    () => videoToBeActivated && videoToBeActivated.length > 0,
    [videoToBeActivated]
  );

  const { styles } = useTheme();
  const backgroundColor = styles.palette.background.default;
  const primary = styles.palette.primary.main;

  React.useEffect(() => setOpenSheet(hasVideoToBeActivated), [hasVideoToBeActivated]);

  const singleVideoActive = videoActive?.length === 1;

  const getLabelText = React.useCallback(() => {
    if (!videoActive?.length) return null;
    return singleVideoActive ? videoActive[0]?.label || '' : content('multiple_agami_active');
  }, [videoActive, singleVideoActive, content]);

  const labelText = React.useMemo(() => getLabelText(), [getLabelText]);

  return (
    <>
      <Box
        position="absolute"
        width="100%"
        bgcolor={primary}
        bottom={0}
        borderRadius="5px 5px 0 0"
        zIndex={999}
        aria-hidden={!isVideoActive}
        style={{
          opacity: isVideoActive ? 1 : 0,
          pointerEvents: isVideoActive ? 'all' : 'none',
          transform: isVideoActive ? 'scale(1) translate(0px, 0px)' : 'scale(0.8) translate(0px, 100px)',
          transition: 'all .5s ease-in-out',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => setOpenSheet(true)}
          aria-label={content('aria_open_video_player')}
          tabIndex={isVideoActive ? 0 : -1}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 8,
            }}
          >
            <PiVideoCamera style={{ fontSize: '1.5rem' }} aria-hidden="true" />

            {labelText && <span aria-live="polite">{labelText}</span>}
          </span>
        </Button>
      </Box>

      <Dialog
        fullScreen
        open={openSheet}
        backgroundColor={backgroundColor}
        aria-label={content('aria_active_video_dialog')}
      >
        <Box position="absolute" right={4} top={4}>
          <IconButton onClick={() => setOpenSheet(false)} aria-label={content('aria_close_video_player')}>
            <PiXCircle aria-hidden="true" />
          </IconButton>
        </Box>

        <Grid container columnGap={2} width="100%" height="100%" padding={2} alignItems="center">
          {videoToBeActivated.map((video) => (
            <Grid item key={video.spotId} textAlign="center" position="relative" width="100%">
              <SingleVideoPlayer video={video} videoSeeksMapRef={videoSeeksMapRef} />
            </Grid>
          ))}
        </Grid>
      </Dialog>
    </>
  );
};

export default VideoPlayerOnMap;
